import { jupiterFetch } from '@svs/nova-core/jupiter';

import type { DataTransferObject } from '../../../@types/DataTransferObject.js';
import { RequestPaymentError } from '../../../error/RequestPaymentError.js';
import { type Wager } from '../../../wager/Wager.js';
import { createPaymentDTO } from '../utils/createPaymentDTO.js';
import {
  type ErrorResponse,
  isErrorResponse,
  type MarketplaceWagerApiResponse,
  type MarketplaceWagerResponse,
} from '../utils/types.js';

function normalizeMarketplaceResponse(data: MarketplaceWagerApiResponse): MarketplaceWagerResponse {
  const { serial, betAmount } = data.CreateWagerReply;
  return {
    ...data,
    wagerClientSerial: serial,
    wagerAmounts: {
      betAmount,
      totalAmount: betAmount,
    },
  };
}

/**
 * Posts a wager for marketplace.
 *
 * @param wager The wager to post
 * @returns The payment data transfer object
 */
export async function postMarketplaceWager(wager: Wager): Promise<DataTransferObject> {
  try {
    const { activatedDrawId } = wager;
    const response = await jupiterFetch(`/marketplace/1/activateddraws/${activatedDrawId}/wagers`, {
      method: 'POST',
      body: JSON.stringify(wager),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'include',
      cache: 'no-store',
    });

    const data = (await response.json()) as MarketplaceWagerApiResponse | ErrorResponse;

    if (!response.ok || isErrorResponse(data)) {
      let code = '';
      let message = 'Unknown error';
      if (isErrorResponse(data)) {
        code = data.error.code;
        message = data.error.message ?? 'Unknown error';
      }

      throw new RequestPaymentError({
        clientCode: 'PAYMENT_REQUEST_FAILURE',
        code,
        extraData: { message },
      });
    }

    const normalizedData = normalizeMarketplaceResponse(data);
    return createPaymentDTO(wager, normalizedData);
  } catch (error) {
    if (error instanceof Error && 'code' in error && typeof error.code === 'string') {
      switch (error.name) {
        case 'TypeError':
          throw new RequestPaymentError({
            clientCode: 'PAYMENT_FETCH_FAILURE',
            code: error.code,
            extraData: { message: error.message },
          });
        default:
          throw error;
      }
    } else {
      throw error;
    }
  }
}
